import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef } from 'react';
import {
	ALL_FILE_TYPES,
	AWS_ALIAS,
	CLOUD_CONVERT_STATUS,
	IMG_TYPES,
	MAX_FILE_SIZE,
	MEDIA_UPLOAD_ERROR_TYPES,
	NON_DOC_FILE_TYPES,
	UPLOAD_STATUS,
	UPLOAD_TYPES,
	VID_TYPES,
} from '../types/constants';
import {
	buildMediaItem,
	buildMediaUrlPrefix,
	checkFileName,
	createCloudConvertJob,
	createMediaFile,
	getFileExtensionGivenFile,
	mediaUploadError,
	resizeImage,
	uploadImageVideoToS3,
	uploadMediaToApi,
} from '../utils/media';
import { GreenroomState } from '../stores/types/types';
import * as MediaActions from '../stores/actions/media';
import { setActiveSubTab } from '../stores/actions/session';
import {
	GetUploadEndpoint,
	S3_SUB_DIRECTORY,
	UploadCloudConvert,
	UploadToS3,
} from '../connection/media';
import getVideoThumbnail from '../utils/video-thumbnail-generator';
import getImageThumbnail from '../utils/image-thumbnail-generator';
import { MediaTabOptionTypes, TABS } from '../stores/reducers/session';
import Socket from '../connection/socket';
import { MediaItem } from '../stores/reducers/media';

export const UPLOAD_ERRORS = {
	GENERIC: 1,
	SERVICE_FAILED: 2, // aws or cloud convert failed
	FILE_TYPE: 3, // file type not supported
	CHARACTERS: 4, // special characters in filename not allowed
};

export const useMediaUpload = (socket?: Socket | null) => {
	const dispatch = useDispatch();
	const meeting = useSelector((state: GreenroomState) => state.SessionReducer.meeting);
	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const currentlyUploadingMedia = useSelector(
		(state: GreenroomState) => state.MediaReducer.currentlyUploadingMedia,
	);
	const selectedMedia = useSelector((state: GreenroomState) => state.MediaReducer.selectedMedia);
	const currentlyUploadingInModal = useSelector(
		(state: GreenroomState) => state.MediaReducer.currentlyUploadingInModal,
	);
	// Had to add useRef here because in handleUpload currentlyUploadingMedia]
	// was getting set but later in the function that same variable was still undefined
	const currentlyUploadingMediaRef = useRef(undefined as MediaItem | undefined);

	useEffect(() => {
		currentlyUploadingMediaRef.current = currentlyUploadingMedia;
	}, [currentlyUploadingMedia]);

	async function doUploadMedia(
		isForModal: boolean,
		files: FileList,
		onUploadInit: Function,
	) {
		// Please consider when devving! This is a duplicate of a
		// function in media-modal.tsx. Change both.
		if (!meeting || !user || !token || (isForModal && !selectedMedia)) {
			return;
		}

		if (files[0].size > MAX_FILE_SIZE) {
			mediaUploadError(MEDIA_UPLOAD_ERROR_TYPES.FILE_SIZE, `The maximum file size for uploads is 5GB. The file ${files[0].name} is ${(files[0].size / 1024 / 1024 / 1024).toFixed(1)}GB`);
			return;
		}

		// the directory this file will live at in aws...i think
		let prefix;
		if (isForModal && selectedMedia) {
			// Prefix was already created when the initial media item was created. Keep consistent.
			prefix = selectedMedia.endpoint.replace(`${AWS_ALIAS}/`, '');
		} else {
			prefix = buildMediaUrlPrefix(meeting.channel, meeting.meeting_id);
		}

		const fileExtension = getFileExtensionGivenFile(files[0]);

		let cloudConvertVideoFlags = null;
		if (VID_TYPES.includes(fileExtension)) {
			cloudConvertVideoFlags = { convertVideoBitrate: true, convertToWebM: false };
		}

		if (ALL_FILE_TYPES.some((ex) => String(ex) === fileExtension)) {
			dispatch(setActiveSubTab(TABS.MEDIA, MediaTabOptionTypes.Files));
		}

		if (selectedMedia) {
			dispatch(MediaActions.setCurrentlyUploadingInModal(
				[...currentlyUploadingInModal, selectedMedia.id],
			));
		}

		const len = files.length;

		switch (fileExtension) {
			case 'jpg':
			case 'png':
			case 'jpeg':
			case 'gif':
			case 'mov':
			case 'webm':
			case 'mp4': {
				const imageUrls: Array<string> = []; // array of images we want to append to our media item

				// creates a MediaItem object
				const newMedia = buildMediaItem(files[0], null, user.gr_user, prefix, meeting.meeting);

				// prepends a UUID to each file's file.name
				const list = new DataTransfer();
				Array.from(files).forEach((file) => list.items.add(createMediaFile(file)));
				const filesWithRandomKey: FileList = list.files;

				// this is set up to allow for multiple files selected,
				// but I don't think we want to do this right now
				for (let i = 0; i < len; i += 1) {
					const secondFileExtension = getFileExtensionGivenFile(files[i]);

					// Ran into a thumbnail issue just with converted mp4s. For just s3 uploads
					// (jpg, png, etc...) we strip all the special
					// chars out above ^. For just Cloud Convert we can pass it anything,
					// but we can't modify the filename before sending
					// the FileList. Converted mp4s use CC and also create the thumbnail below
					// and upload directly to S3. Because of the way thumbnails are
					// loaded based off of the "images" column in gr_media, we can't strip the
					// special chars off of the filename in just
					// this case because Cloud Convert will return a different filepath for "images".
					// So unless we refactor the way we load the thumbnails
					// images (maybe add a thumbnails column) we just don't allow special chars
					// for converted mp4s for now
					if (!checkFileName(files[i].name)) {
						// eslint-disable-next-line no-continue
						continue;
					}

					if (secondFileExtension && NON_DOC_FILE_TYPES?.includes(secondFileExtension)) {
						// Normally for .mp4 videos we upload directly to S3. However,
						// if the bitrate is too high we instead convert
						// the .mp4 using Cloud Convert to bring the bitrate down
						// which then exports to S3 from CC (same as pdf, ppt, in case below)
						if (cloudConvertVideoFlags?.convertVideoBitrate) {
							// eslint-disable-next-line no-await-in-loop
							const cloudConvertUpload = await createCloudConvertJob(
								token,
								filesWithRandomKey[0],
								prefix,
								cloudConvertVideoFlags?.convertToWebM,
								't:media',
							);
							if (cloudConvertUpload) {
								if (isForModal) {
									const newMediaPage = {
										...selectedMedia,
										cloud_convert_id: cloudConvertUpload.id,
										convert_status: CLOUD_CONVERT_STATUS.IN_PROGRESS,
										edited_by: user.gr_user,
									} as MediaItem;
									// eslint-disable-next-line no-await-in-loop
									const cloudConvertUploaded = await UploadCloudConvert(
										cloudConvertUpload,
										filesWithRandomKey,
										(request) => {
											onUploadInit(
												newMediaPage.original_name,
												UPLOAD_TYPES.PAGE,
												newMediaPage.endpoint,
												request,
											);
										},
									);
									if (cloudConvertUploaded === UPLOAD_STATUS.ERROR) {
										mediaUploadError(MEDIA_UPLOAD_ERROR_TYPES.FILE_UPLOAD);
									} else if (cloudConvertUploaded === UPLOAD_STATUS.LOAD) {
										dispatch(MediaActions.saveMediaItem(
											token,
											newMediaPage,
											undefined,
										));
									}
								} else {
									// eslint-disable-next-line no-await-in-loop
									const cloudConvertUploaded = await UploadCloudConvert(
										cloudConvertUpload,
										filesWithRandomKey,
										(request) => {
											onUploadInit(
												newMedia.original_name,
												UPLOAD_TYPES.MEDIA,
												newMedia.endpoint,
												request,
											);
										},
									);
									if (cloudConvertUploaded === UPLOAD_STATUS.ERROR) {
										mediaUploadError(MEDIA_UPLOAD_ERROR_TYPES.FILE_UPLOAD);
									} else if (cloudConvertUploaded === UPLOAD_STATUS.LOAD) {
										newMedia.cloud_convert_id = cloudConvertUpload.id;
										newMedia.endpoint = cloudConvertUpload.endpoint;
										dispatch(MediaActions.createMedia(token, newMedia));
									}
								}
							}
						} else {
							let fileToUpload = filesWithRandomKey[i];
							if (IMG_TYPES.includes(secondFileExtension)) {
								// resize an image if it exceeds 4K width (3840 pixels) or height (2160 pixels)
								// eslint-disable-next-line no-await-in-loop
								fileToUpload = await resizeImage(
									filesWithRandomKey[i],
									secondFileExtension,
									3840,
									2160,
								);
							}
							// Upload to S3 like normal
							// Upload an image file, OR a video file IF it has not been
							// flagged by convertVideoBitrate
							// uploads a file to aws and returns the url that file lives at
							// eslint-disable-next-line no-await-in-loop
							const imageUrl = await uploadImageVideoToS3(
								fileToUpload,
								prefix,
								token,
								(request) => {
									onUploadInit(
										newMedia.original_name,
										isForModal ? UPLOAD_TYPES.PAGE : UPLOAD_TYPES.MEDIA,
										newMedia.endpoint,
										request,
									);
								},
							);
							if (imageUrl) {
								imageUrls.push(imageUrl);
							} else {
								// if no imageUrl, the upload request was aborted
								return;
							}
						}

						// Thumbnails for .mov files will be created by Cloud Convert.
						if (fileExtension !== 'mov') {
							// WebM files without a set duration will cause the
							// getVideoThumbnail function to error.
							if (fileExtension === 'webm' && cloudConvertVideoFlags?.convertVideoBitrate) return;

							// Thumbnails for mp4s and everything else in this block get created
							// on the client side (cloud convert
							// can't create thumbnail for mp4, so we do it here unlike pdf,
							// ppt which CC can create)

							const thumb = fileExtension === 'mp4' || fileExtension === 'webm'
								// eslint-disable-next-line no-await-in-loop
								? await getVideoThumbnail(filesWithRandomKey[i], false) as Blob
								// eslint-disable-next-line no-await-in-loop
								: await getImageThumbnail(filesWithRandomKey[i]) as Blob;

							// Whenever thumb is false, it will throw out an error
							// message and clear/remove the media data
							if (!thumb) {
								mediaUploadError(MEDIA_UPLOAD_ERROR_TYPES.FILE_UPLOAD);
								return;
							}

							const thumbName = filesWithRandomKey[i].name.replace(/(.mp4|.mov|.webm|.jpeg|.gif|.png)$/, fileExtension === 'webm' ? '.png' : '.jpg');
							// eslint-disable-next-line no-await-in-loop
							const thumbEndpoint = await GetUploadEndpoint(
								token,
								prefix,
								thumbName,
								S3_SUB_DIRECTORY.THUMBS,
							);
							// eslint-disable-next-line no-await-in-loop
							await UploadToS3(thumbEndpoint.url, thumb, thumbEndpoint.fields, thumbName);
						}
					} else {
						mediaUploadError(MEDIA_UPLOAD_ERROR_TYPES.FILE_TYPE, `You can only upload files of mixed types with videos and images. Skipped: ${filesWithRandomKey[i]}`);
					}
				}

				// S3 new media item uploads
				if (
					!isForModal
					&& !cloudConvertVideoFlags?.convertVideoBitrate
				) {
					// the media item will be concatted onto our media array after the promise is returned
					newMedia.images = imageUrls.join('');
					dispatch(MediaActions.createMedia(token, newMedia));
				}

				// S3 additional page uploads
				if (
					isForModal
					&& !cloudConvertVideoFlags?.convertVideoBitrate
					&& selectedMedia?.images
				) {
					const images = [...selectedMedia.media_data.image_array, ...imageUrls];
					dispatch(MediaActions.saveMediaItem(
						token,
						{
							...selectedMedia,
							images: images.join(','),
							num_pages: images.length,
							edited_by: user.gr_user,
						},
						undefined,
					));
					socket?.setMedia({
						...selectedMedia, images: images.join(','), num_pages: images.length, edited_by: user.gr_user,
					});
				}

				break;
			}
			case 'pdf': {
				if (isForModal) {
					mediaUploadError(MEDIA_UPLOAD_ERROR_TYPES.FILE_TYPE, 'Documents are not supported for additional pages in media decks');
					return;
				}

				/*
				Ran into a thumbnail issue just with converted mp4s. For just s3 uploads
				(jpg, png, etc...) we strip all the special
				chars out above ^. For just Cloud Convert we can pass it anything,
				but we can't modify the filename before sending
				the FileList. Converted mp4s use CC and also create the thumbnail
				below and upload directly to S3. Because of the way thumbnails are
				loaded based off of the "images" column in gr_media, we can't strip
				the special chars off of the filename in just
				this case because Cloud Convert will return a different filepath for
				"images". So unless we refactor the way we load the thumbnails
				images (maybe add a thumbnails column) we just don't allow special
				chars for converted mp4s for now
				*/

				if (!checkFileName(files[0].name)) {
					return;
				}

				const cloudConvertUpload = await createCloudConvertJob(
					token,
					files[0],
					prefix,
					undefined,
					't:media',
				);

				if (cloudConvertUpload && cloudConvertUpload.id) {
					// Create a temporary media link in the UI that shows the
					// loading indicator with the name of the file so the
					// user sees progress happening
					const newMedia = buildMediaItem(
						files[0],
						cloudConvertUpload,
						user.gr_user,
						prefix,
						meeting.meeting,
					);

					const cloudConvertUploaded = await UploadCloudConvert(
						cloudConvertUpload,
						files,
						(request) => {
							onUploadInit(
								newMedia.original_name,
								UPLOAD_TYPES.MEDIA,
								newMedia.endpoint,
								request,
							);
						},
					);
					if (cloudConvertUploaded === UPLOAD_STATUS.ERROR) {
						mediaUploadError(MEDIA_UPLOAD_ERROR_TYPES.FILE_UPLOAD);
					} else if (cloudConvertUploaded === UPLOAD_STATUS.LOAD) {
						newMedia.cloud_convert_id = cloudConvertUpload.id;
						newMedia.endpoint = cloudConvertUpload.endpoint;
						dispatch(MediaActions.createMedia(token, newMedia));
					}
				} else {
					mediaUploadError(MEDIA_UPLOAD_ERROR_TYPES.FILE_TYPE, 'Documents are not supported for additional pages in media decks');
				}
				break;
			}
			case 'aac':
			case 'mp3':
			case 'wav':
			case 'key':
			case 'pptx': {
				if (isForModal) {
					mediaUploadError(MEDIA_UPLOAD_ERROR_TYPES.FILE_TYPE, 'Documents are not supported for additional pages in media decks');
					return;
				}

				const newMedia = buildMediaItem(
					files[0],
					null,
					user.gr_user,
					prefix,
					meeting.meeting,
				);

				const result = await uploadMediaToApi(
					token,
					meeting.meeting,
					files,
					(request) => {
						onUploadInit(
							newMedia.original_name,
							UPLOAD_TYPES.MEDIA,
							newMedia.endpoint,
							request,
						);
					},
				);

				if (result === UPLOAD_STATUS.ERROR) {
					mediaUploadError(MEDIA_UPLOAD_ERROR_TYPES.FILE_UPLOAD, 'There was an error uploading your file, please try again.');
				}
				break;
			}
			default: break;
		}
	}

	return { doUploadMedia };
};
